/**
 * Status bar customization store (Faz 4.1 — OCS StatusBarConfig parity).
 *
 * OCS keeps a `StatusBarConfig { hidden: FxHashSet<StatusPill> }` in the app
 * config; yap mirrors it as a persisted `hiddenPills` id list consumed by the
 * shared StatusBar renderer. The OCS 21-pill set maps onto yap surfaces —
 * Scale / Anno* / Vp pills stay registered but render only once their owning
 * phase ships (annotative store in Faz 8), and SpaceMouse waits for the Faz 10
 * input layer, so their visibility conditions read from the stores instead of
 * being hardcoded rows.
 *
 * The coordinate readout mode rides here too ($COORDS 0/1/2: last-click
 * static / live absolute / live polar), cycled by clicking the Coords pill.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../utils/settingsPersistence';

/** Stable pill ids — the customization menu rows key on these. */
export type StatusPillId =
    | 'coords'
    | 'ortho'
    | 'lwt'
    | 'polar'
    | 'dyn'
    | 'otrack'
    | 'osnap'
    | 'space'
    | 'units'
    | 'transparency'
    | 'isolate'
    | 'quickProps'
    | 'selFilter'
    | 'selCycle'
    | 'cleanScreen'
    // Annotative family (Faz 8.12): gated by the annotation scale store.
    | 'annoVisibility'
    | 'annoAutoAdd'
    | 'vpScaleSync';

export type CoordsMode = 0 | 1 | 2;

interface StatusBarState {
    /** Hidden pill ids (persisted — OCS StatusBarConfig.hidden). */
    hiddenPills: StatusPillId[];
    /** $COORDS parity readout mode: 0 static, 1 live absolute, 2 polar. */
    coordsMode: CoordsMode;

    togglePill: (id: StatusPillId) => void;
    setPillHidden: (id: StatusPillId, hidden: boolean) => void;
    cycleCoordsMode: () => void;
    setCoordsMode: (mode: CoordsMode) => void;
}

export const useStatusBarStore = create<StatusBarState>()(
    persist(
        (set, get) => ({
            hiddenPills: [],
            coordsMode: 1,

            togglePill: (id) => set((s) => ({
                hiddenPills: s.hiddenPills.includes(id)
                    ? s.hiddenPills.filter((p) => p !== id)
                    : [...s.hiddenPills, id],
            })),
            setPillHidden: (id, hidden) => set((s) => ({
                hiddenPills: hidden
                    ? s.hiddenPills.includes(id) ? s.hiddenPills : [...s.hiddenPills, id]
                    : s.hiddenPills.filter((p) => p !== id),
            })),
            cycleCoordsMode: () => set((s) => ({ coordsMode: (((s.coordsMode + 1) % 3) as CoordsMode) })),
            setCoordsMode: (mode) => {
                if (get().coordsMode !== mode) set({ coordsMode: mode });
            },
        }),
        {
            name: 'yap-statusbar-config',
            storage: settingsStorage,
            // Only the customization set survives restarts; the coords mode
            // is a per-session reading preference (classic $COORDS semantics).
            partialize: (s) => ({ hiddenPills: s.hiddenPills }),
        }
    )
);
